import { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { gpsToLocal, localToGps, type GpsOrigin } from "@/lib/gps-to-local";
import type { Pt } from "./footprint";

/**
 * 사이트 원점 기준 베이스맵 — Carto dark 래스터 타일(키리스)을 한 장으로 합성해
 * 3D 부지 아래 바닥 평면으로 깐다. 마커/모델과 동일한 `gpsToLocal` 투영을 써서 정합.
 *
 * footprintPolygon(건물 바닥 윤곽, 로컬 XZ)을 주면 그 폴리곤을 컷아웃 — 지하·저고도
 * 콘텐츠가 지도에 가려지지 않게 한다(부지 바깥만 지도로 채움). 바운딩박스가 아니라
 * 실제 모델 윤곽(convex hull)이라 베이스 둘레 여백이 거의 없다.
 */

interface BaseMapProps {
  origin: GpsOrigin;
  /** 원점 기준 ±radius(m) 범위를 덮는다. */
  radius?: number;
  /** 래스터 타일 줌 레벨 (17 ≈ 1m/px). */
  zoom?: number;
  /** 지면 높이(살짝 아래로 깔아 모델 슬래브와 z-fighting 방지). */
  y?: number;
  /** 건물 바닥 윤곽(로컬 XZ) — 주면 컷아웃, 없으면 전체 평면. */
  footprintPolygon?: Pt[] | null;
  /**
   * 타일 스타일 (모두 키리스).
   * - light_all/dark_all/voyager: Carto 베이스맵
   * - satellite: Esri World Imagery(위성). 출처표기 © Esri, Maxar, Earthstar Geographics.
   */
  style?: "light_all" | "dark_all" | "voyager" | "satellite";
}

const TILE = 256;
// 키리스·CORS 클린(WebGL 텍스처 사용 가능).
// - Carto: z/x/y  (출처 © OpenStreetMap, © CARTO)
// - Esri World Imagery 위성: z/y/x  (출처 © Esri, Maxar, Earthstar Geographics)
const tileUrl = (style: string, z: number, x: number, y: number) =>
  style === "satellite"
    ? `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`
    : `https://basemaps.cartocdn.com/${style}/${z}/${x}/${y}.png`;

/** lon/lat → Web Mercator 분수 타일 좌표 */
function lonLatToTile(lon: number, lat: number, z: number) {
  const n = 2 ** z;
  const latRad = (lat * Math.PI) / 180;
  return {
    x: ((lon + 180) / 360) * n,
    y: ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n,
  };
}

/** 정수 타일의 북서(NW) 코너 → lon/lat */
function tileToLonLat(x: number, y: number, z: number) {
  const n = 2 ** z;
  return {
    lon: (x / n) * 360 - 180,
    lat: (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI,
  };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`tile load failed: ${src}`));
    img.src = src;
  });
}

interface Composite {
  texture: THREE.CanvasTexture;
  /** 합성 이미지가 덮는 지리 경계의 로컬 좌표 (NW=서북, SE=동남) */
  xW: number;
  xE: number;
  zN: number;
  zS: number;
}

export function BaseMap({
  origin,
  radius = 400,
  zoom = 17,
  y = -0.05,
  footprintPolygon = null,
  style = "light_all",
}: BaseMapProps) {
  const [comp, setComp] = useState<Composite | null>(null);

  useEffect(() => {
    let cancelled = false;

    // 원점 ±radius의 지리 경계 (NW=서북, SE=동남)
    const nw = localToGps(origin, -radius, -radius);
    const se = localToGps(origin, radius, radius);

    const tNW = lonLatToTile(nw.lon, nw.lat, zoom);
    const tSE = lonLatToTile(se.lon, se.lat, zoom);
    const xMin = Math.floor(tNW.x);
    const xMax = Math.floor(tSE.x);
    const yMin = Math.floor(tNW.y);
    const yMax = Math.floor(tSE.y);
    const cols = xMax - xMin + 1;
    const rows = yMax - yMin + 1;
    // 비정상 타일 수(과대 범위 등)면 합성을 건너뛴다.
    if (cols < 1 || rows < 1 || cols * rows > 100) return;

    const canvas = document.createElement("canvas");
    canvas.width = cols * TILE;
    canvas.height = rows * TILE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#0a0e14";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const jobs: Promise<void>[] = [];
    for (let ty = yMin; ty <= yMax; ty++) {
      for (let tx = xMin; tx <= xMax; tx++) {
        const col = tx - xMin;
        const row = ty - yMin;
        jobs.push(
          loadImage(tileUrl(style, zoom, tx, ty))
            .then((img) => {
              if (!cancelled) ctx.drawImage(img, col * TILE, row * TILE);
            })
            .catch(() => {
              /* 개별 타일 실패는 무시 (배경색 유지) */
            })
        );
      }
    }

    // 합성 이미지가 실제로 덮는 지리 경계(타일 정렬) → 로컬
    const compNW = tileToLonLat(xMin, yMin, zoom);
    const compSE = tileToLonLat(xMax + 1, yMax + 1, zoom);
    const locNW = gpsToLocal(origin, compNW.lon, compNW.lat);
    const locSE = gpsToLocal(origin, compSE.lon, compSE.lat);

    Promise.all(jobs).then(() => {
      if (cancelled) return;
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 4;
      texture.minFilter = THREE.LinearFilter;
      texture.needsUpdate = true;
      setComp({ texture, xW: locNW.x, xE: locSE.x, zN: locNW.z, zS: locSE.z });
    });

    return () => {
      cancelled = true;
    };
  }, [origin, radius, zoom, style]);

  // 텍스처 GPU 메모리 해제 — comp 교체/언마운트 시 이전 CanvasTexture dispose (메모리 누수 방지)
  useEffect(() => {
    return () => {
      comp?.texture.dispose();
    };
  }, [comp]);

  // 지면 지오메트리 — 사각 평면(footprint 없을 때) 또는 폴리곤 컷아웃(있을 때).
  // Shape 좌표계: sx = localX(동), sy = -localZ(북이 +y). 회전(-π/2 X) 후 로컬 XZ에 정합.
  const geometry = useMemo(() => {
    if (!comp) return null;
    const { xW, xE, zN, zS } = comp;
    const shape = new THREE.Shape();
    shape.moveTo(xW, -zS);
    shape.lineTo(xE, -zS);
    shape.lineTo(xE, -zN);
    shape.lineTo(xW, -zN);
    shape.closePath();

    if (footprintPolygon && footprintPolygon.length >= 3) {
      const hole = new THREE.Path();
      footprintPolygon.forEach((p, i) => {
        if (i === 0) hole.moveTo(p.x, -p.z);
        else hole.lineTo(p.x, -p.z);
      });
      hole.closePath();
      shape.holes.push(hole);
    }

    const geom = new THREE.ShapeGeometry(shape);
    // 합성 텍스처 UV 재계산 (position → uv). v=1이 북(zN).
    const pos = geom.attributes.position as THREE.BufferAttribute;
    const uv = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i++) {
      const sx = pos.getX(i);
      const sy = pos.getY(i); // = -localZ
      uv[i * 2] = (sx - xW) / (xE - xW);
      uv[i * 2 + 1] = (zS + sy) / (zS - zN);
    }
    geom.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
    return geom;
  }, [comp, footprintPolygon]);

  // 지오메트리 GPU 메모리 해제 — imperative 생성이라 R3F 자동 dispose 대상 아님.
  // geometry 교체(comp/footprint 변경)·언마운트 시 이전 ShapeGeometry dispose.
  useEffect(() => {
    return () => {
      geometry?.dispose();
    };
  }, [geometry]);

  if (!comp || !geometry) return null;

  return (
    <mesh geometry={geometry} rotation={[-Math.PI / 2, 0, 0]} position={[0, y, 0]}>
      <meshBasicMaterial map={comp.texture} toneMapped={false} />
    </mesh>
  );
}
